import React, { memo, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  InputAdornment,
  Portal,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AccountTree as GraphIcon,
  Autorenew as RefreshIcon,
  Build as ToolIcon,
  Dangerous as DangerousIcon,
  FilterAltOff as ClearFilterIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Search as SearchIcon,
  SmartToy as AgentIcon,
} from '@mui/icons-material';
import {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  type Edge,
  type Node,
  type NodeProps,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { agentApi } from '../../../services/api';
import type {
  AgentDetail as AgentDetailType,
  AgentToolAssignment,
  AvailableSubAgents,
  AvailableTools,
  SubAgentAssignment,
  Tool,
} from '../../../types/agent';
import { SectionCard } from './primitives';

const MAX_GRAPH_DEPTH = 20;
const MAX_GRAPH_AGENTS = 150;
const AGENT_NODE_WIDTH = 280;
const AGENT_NODE_HEIGHT = 112;
const TOOL_NODE_WIDTH = 210;
const TOOL_NODE_HEIGHT = 104;
const TOOL_LAYER_X_GAP = 380;
const TOOL_LAYER_Y_GAP = 210;
const CHILD_AGENT_X_GAP = 1320;
const CHILD_AGENT_Y_GAP = 520;
const ROOT_SUB_AGENT_ORBIT_RADIUS = 1680;
const CHILD_SUB_AGENT_ORBIT_RADIUS = 1240;
const ROOT_TOOL_LAYER_GAP = 700;
const CHILD_TOOL_LAYER_GAP = 540;
const CATEGORY_LAYER_GAP = 420;
const NODE_MIN_GAP = 150;
const COLLISION_ITERATIONS = 220;
const CATEGORY_COLORS = [
  '#f59e0b',
  '#22c55e',
  '#38bdf8',
  '#a78bfa',
  '#fb7185',
  '#2dd4bf',
  '#84cc16',
  '#f97316',
  '#60a5fa',
  '#e879f9',
  '#14b8a6',
  '#facc15',
];

type AgentNodeKind = 'root' | 'agent' | 'tool';

interface GraphAgent {
  id: number;
  name: string;
  description?: string;
  agent_type?: string;
  is_active?: boolean;
  model_name?: string | null;
  depth: number;
}

interface ToolConnection {
  id: string;
  parentAgentId: number;
  assignment: AgentToolAssignment;
  tool?: Tool;
  depth: number;
}

interface SubAgentConnection {
  id: string;
  parentAgentId: number;
  subAgentId: number;
  assignment: SubAgentAssignment;
  depth: number;
  repeated: boolean;
}

interface AgentGraphData {
  agents: GraphAgent[];
  toolConnections: ToolConnection[];
  subAgentConnections: SubAgentConnection[];
  warnings: string[];
  truncated: boolean;
}

interface VisualizerNodeData {
  kind: AgentNodeKind;
  title: string;
  subtitle?: string;
  description?: string;
  active?: boolean;
  enabled?: boolean;
  dangerous?: boolean;
  category?: string;
  usageCount?: number;
  depth?: number;
  color?: string;
}

interface AgentVisualizerCanvasProps {
  agent: AgentDetailType;
}

const getAgentTypeLabel = (value?: string) =>
  (value || 'ASSISTANT').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const getToolCategoryLabel = (value?: string) =>
  (value || 'tool').replace(/[_-]/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const hashString = (value: string) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
};

const getCategoryColor = (category?: string) =>
  CATEGORY_COLORS[hashString(category || 'assigned_tools') % CATEGORY_COLORS.length];

const normalizeNeedle = (value: string) => value.trim().toLowerCase();

const matchesText = (value: string | undefined | null, needle: string) =>
  !needle || (value || '').toLowerCase().includes(needle);

const getApiMessage = (error: any) =>
  error?.response?.data?.detail ||
  error?.response?.data?.error ||
  error?.message ||
  'Unable to load graph data';

const buildAgentGraph = async (
  rootAgent: AgentDetailType,
  includeDisabled: boolean
): Promise<AgentGraphData> => {
  const agents = new Map<number, GraphAgent>();
  const toolConnections: ToolConnection[] = [];
  const subAgentConnections: SubAgentConnection[] = [];
  const visited = new Set<number>();
  const warnings: string[] = [];
  let truncated = false;

  const upsertAgent = (agent: GraphAgent) => {
    const existing = agents.get(agent.id);
    if (!existing || agent.depth < existing.depth) {
      agents.set(agent.id, { ...existing, ...agent });
    }
  };

  const loadAgent = async (agent: GraphAgent, depth: number): Promise<void> => {
    upsertAgent({ ...agent, depth });

    if (visited.has(agent.id)) return;
    if (agents.size > MAX_GRAPH_AGENTS) {
      truncated = true;
      warnings.push(`Graph stopped after ${MAX_GRAPH_AGENTS} agent nodes.`);
      return;
    }
    if (depth > MAX_GRAPH_DEPTH) {
      truncated = true;
      warnings.push(`Graph stopped after ${MAX_GRAPH_DEPTH} nested sub-agent levels.`);
      return;
    }

    visited.add(agent.id);

    let toolsData: AvailableTools | null = null;
    let subAgentsData: AvailableSubAgents | null = null;

    try {
      [toolsData, subAgentsData] = await Promise.all([
        agentApi.getTools(agent.id),
        agentApi.getSubAgents(agent.id),
      ]);
    } catch (error) {
      warnings.push(`${agent.name}: ${getApiMessage(error)}`);
      return;
    }

    const toolsByName = new Map<string, Tool>();
    (toolsData?.available || []).forEach((tool) => toolsByName.set(tool.name, tool));

    (toolsData?.assigned || [])
      .filter((assignment) => includeDisabled || assignment.is_enabled)
      .forEach((assignment) => {
        toolConnections.push({
          id: `tool-edge-${agent.id}-${assignment.id || assignment.tool_name}`,
          parentAgentId: agent.id,
          assignment,
          tool: toolsByName.get(assignment.tool_name),
          depth,
        });
      });

    const childAssignments = (subAgentsData?.assigned || []).filter(
      (assignment) => includeDisabled || assignment.is_enabled
    );

    await Promise.all(
      childAssignments.map(async (assignment) => {
        const child = assignment.sub_agent_details;
        const childAgent: GraphAgent = {
          id: assignment.sub_agent,
          name: child?.name || `Agent #${assignment.sub_agent}`,
          description: child?.description || assignment.effective_tool_description,
          agent_type: child?.agent_type || 'ASSISTANT',
          is_active: child?.is_active,
          depth: depth + 1,
        };
        const repeated = visited.has(childAgent.id);

        upsertAgent(childAgent);
        subAgentConnections.push({
          id: `sub-agent-edge-${agent.id}-${assignment.id || assignment.sub_agent}`,
          parentAgentId: agent.id,
          subAgentId: childAgent.id,
          assignment,
          depth,
          repeated,
        });

        if (repeated) return;
        await loadAgent(childAgent, depth + 1);
      })
    );
  };

  await loadAgent(
    {
      id: rootAgent.id,
      name: rootAgent.name,
      description: rootAgent.description,
      agent_type: rootAgent.agent_type,
      is_active: rootAgent.is_active,
      model_name: rootAgent.model?.name || rootAgent.model_name,
      depth: 0,
    },
    0
  );

  return {
    agents: Array.from(agents.values()).sort((a, b) => a.depth - b.depth || a.name.localeCompare(b.name)),
    toolConnections,
    subAgentConnections,
    warnings: Array.from(new Set(warnings)),
    truncated,
  };
};

const sanitizeId = (value: string | number) =>
  String(value).replace(/[^a-zA-Z0-9_-]+/g, '-');

const getNodeSize = (kind: AgentNodeKind) => {
  if (kind === 'tool') return { width: TOOL_NODE_WIDTH, height: TOOL_NODE_HEIGHT };
  return { width: AGENT_NODE_WIDTH, height: AGENT_NODE_HEIGHT };
};

const toTopLeft = (center: { x: number; y: number }, kind: AgentNodeKind) => {
  const { width, height } = getNodeSize(kind);
  return {
    x: center.x - width / 2,
    y: center.y - height / 2,
  };
};

const getToolFamily = (toolName: string) => {
  const tokens = toolName.toLowerCase().split(/[_-]+/).filter(Boolean);
  if (tokens.length === 0) return 'tools';
  if (tokens[0] === 'ai' && tokens[1] === 'agent' && tokens[2]) return tokens[2];
  if (tokens[0] === 'agent' && tokens[1]) return tokens[1];
  if (tokens[0] === 'model' && tokens[1] === 'lab' && tokens[2]) return tokens[2];
  if (tokens[0] === 'data' && tokens[1]) return tokens[1];
  return tokens.slice(0, Math.min(2, tokens.length)).join(' ');
};

interface ToolGroup {
  category: string;
  connections: ToolConnection[];
}

const buildToolGroups = (connections: ToolConnection[]): ToolGroup[] => {
  const byCategory = new Map<string, ToolConnection[]>();
  connections.forEach((connection) => {
    const category = connection.tool?.category || 'assigned_tools';
    if (!byCategory.has(category)) byCategory.set(category, []);
    byCategory.get(category)!.push(connection);
  });

  return Array.from(byCategory.entries())
    .map(([category, categoryConnections]) => ({
      category,
      connections: [...categoryConnections].sort((a, b) => {
        const familyCompare = getToolFamily(a.assignment.tool_name).localeCompare(
          getToolFamily(b.assignment.tool_name)
        );
        if (familyCompare !== 0) return familyCompare;
        return a.assignment.tool_name.localeCompare(b.assignment.tool_name);
      }),
    }))
    .sort((a, b) => {
      if (a.connections.length !== b.connections.length) {
        return b.connections.length - a.connections.length;
      }
      return getToolCategoryLabel(a.category).localeCompare(getToolCategoryLabel(b.category));
    });
};

const getToolGroupGrid = (count: number) => {
  const columns = Math.min(4, Math.max(1, Math.ceil(Math.sqrt(count))));
  return {
    columns,
    rows: Math.ceil(count / columns),
  };
};

const layoutToolGroups = (
  parentCenter: { x: number; y: number },
  groups: ToolGroup[],
  isRootAgent: boolean
) => {
  const centers = new Map<string, { x: number; y: number }>();
  const baseLayerGap = isRootAgent ? ROOT_TOOL_LAYER_GAP : CHILD_TOOL_LAYER_GAP;
  const categoriesPerLayer = isRootAgent ? 8 : 6;

  groups.forEach((group, groupIndex) => {
    const { columns, rows } = getToolGroupGrid(group.connections.length);
    const layer = Math.floor(groupIndex / categoriesPerLayer);
    const indexInLayer = groupIndex % categoriesPerLayer;
    const remaining = groups.length - layer * categoriesPerLayer;
    const itemsInLayer = Math.min(categoriesPerLayer, remaining);
    const angle =
      -Math.PI / 2 +
      (indexInLayer * Math.PI * 2) / Math.max(1, itemsInLayer) +
      (layer % 2 === 0 ? 0 : Math.PI / Math.max(3, itemsInLayer));
    const radius = baseLayerGap + layer * (columns * TOOL_LAYER_X_GAP + CATEGORY_LAYER_GAP);
    const radial = {
      x: Math.cos(angle),
      y: Math.sin(angle),
    };
    const tangent = {
      x: -radial.y,
      y: radial.x,
    };
    const groupCenter = {
      x: parentCenter.x + radial.x * radius,
      y: parentCenter.y + radial.y * radius,
    };

    group.connections.forEach((connection, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const centeredColumn = column - (columns - 1) / 2;
      const centeredRow = row - (rows - 1) / 2;

      centers.set(connection.id, {
        x:
          groupCenter.x +
          radial.x * column * TOOL_LAYER_X_GAP +
          tangent.x * centeredRow * TOOL_LAYER_Y_GAP,
        y:
          groupCenter.y +
          radial.y * column * TOOL_LAYER_X_GAP +
          tangent.y * centeredRow * TOOL_LAYER_Y_GAP,
      });
    });
  });

  return centers;
};

const sideForVector = (from: { x: number; y: number }, to: { x: number; y: number }) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'right' : 'left';
  return dy >= 0 ? 'bottom' : 'top';
};

const oppositeSide = (side: string) => {
  if (side === 'right') return 'left';
  if (side === 'left') return 'right';
  if (side === 'bottom') return 'top';
  return 'bottom';
};

const getHandlePair = (
  sourceCenter: { x: number; y: number },
  targetCenter: { x: number; y: number }
) => {
  const sourceSide = sideForVector(sourceCenter, targetCenter);
  return {
    sourceHandle: `source-${sourceSide}`,
    targetHandle: `target-${oppositeSide(sourceSide)}`,
  };
};

const getNodeCenter = (node: Node<VisualizerNodeData>) => {
  const size = getNodeSize(node.data.kind);
  return {
    x: node.position.x + size.width / 2,
    y: node.position.y + size.height / 2,
  };
};

const resolveNodeCollisions = (
  nodes: Node<VisualizerNodeData>[],
  lockedIds: Set<string>
) => {
  const resolved = nodes.map((node) => ({
    ...node,
    position: { ...node.position },
  }));

  for (let iteration = 0; iteration < COLLISION_ITERATIONS; iteration += 1) {
    let moved = false;

    for (let i = 0; i < resolved.length; i += 1) {
      for (let j = i + 1; j < resolved.length; j += 1) {
        const a = resolved[i];
        const b = resolved[j];
        const aSize = getNodeSize(a.data.kind);
        const bSize = getNodeSize(b.data.kind);
        const aCenter = getNodeCenter(a);
        const bCenter = getNodeCenter(b);
        const dx = bCenter.x - aCenter.x;
        const dy = bCenter.y - aCenter.y;
        const overlapX = (aSize.width + bSize.width) / 2 + NODE_MIN_GAP - Math.abs(dx);
        const overlapY = (aSize.height + bSize.height) / 2 + NODE_MIN_GAP - Math.abs(dy);

        if (overlapX <= 0 || overlapY <= 0) continue;

        const aLocked = lockedIds.has(a.id);
        const bLocked = lockedIds.has(b.id);
        if (aLocked && bLocked) continue;

        const resolveOnX = overlapX < overlapY;
        const direction = resolveOnX
          ? dx === 0
            ? i % 2 === 0
              ? 1
              : -1
            : Math.sign(dx)
          : dy === 0
            ? j % 2 === 0
              ? 1
              : -1
            : Math.sign(dy);
        const overlap = resolveOnX ? overlapX : overlapY;
        const aMove = aLocked ? 0 : bLocked ? overlap : overlap / 2;
        const bMove = bLocked ? 0 : aLocked ? overlap : overlap / 2;

        if (resolveOnX) {
          a.position.x -= direction * aMove;
          b.position.x += direction * bMove;
        } else {
          a.position.y -= direction * aMove;
          b.position.y += direction * bMove;
        }
        moved = true;
      }
    }

    if (!moved) break;
  }

  return resolved;
};

const buildNodeCenterMap = (nodes: Node<VisualizerNodeData>[]) => {
  const centers = new Map<string, { x: number; y: number }>();
  nodes.forEach((node) => {
    centers.set(node.id, getNodeCenter(node));
  });
  return centers;
};

const updateEdgeHandles = (
  edges: Edge[],
  centers: Map<string, { x: number; y: number }>
) =>
  edges.map((edge) => {
    const sourceCenter = centers.get(edge.source);
    const targetCenter = centers.get(edge.target);
    if (!sourceCenter || !targetCenter) return edge;
    return {
      ...edge,
      ...getHandlePair(sourceCenter, targetCenter),
    };
  });

const VisualizerNode = memo(({ data }: NodeProps<VisualizerNodeData>) => {
  const theme = useTheme();
  const isTool = data.kind === 'tool';
  const isRoot = data.kind === 'root';
  const muted = data.enabled === false || data.active === false;
  const nodeColor = data.color || (
    isRoot
      ? theme.palette.primary.main
      : isTool
        ? theme.palette.success.main
        : theme.palette.info.main
  );
  const { width, height } = getNodeSize(data.kind);
  const handleStyle = {
    width: 8,
    height: 8,
    border: 0,
    opacity: 0,
    background: nodeColor,
  };

  return (
    <Box
      sx={{
        width,
        height,
        border: 1,
        borderColor: muted ? 'divider' : alpha(nodeColor, 0.45),
        borderRadius: 1.5,
        bgcolor: muted
          ? alpha(theme.palette.text.disabled, 0.06)
          : alpha(nodeColor, theme.palette.mode === 'dark' ? 0.12 : 0.08),
        boxShadow: theme.palette.mode === 'dark'
          ? '0 10px 26px rgba(0,0,0,0.22)'
          : '0 10px 24px rgba(15,23,42,0.08)',
        opacity: muted ? 0.74 : 1,
        p: 1.25,
        overflow: 'hidden',
      }}
    >
      <Handle id="target-left" type="target" position={Position.Left} style={handleStyle} />
      <Handle id="target-right" type="target" position={Position.Right} style={handleStyle} />
      <Handle id="target-top" type="target" position={Position.Top} style={handleStyle} />
      <Handle id="target-bottom" type="target" position={Position.Bottom} style={handleStyle} />
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(nodeColor, 0.14),
              color: nodeColor,
              flexShrink: 0,
            }}
          >
            {isTool ? <ToolIcon fontSize="small" /> : <AgentIcon fontSize="small" />}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight={800}
              sx={{
                lineHeight: 1.2,
                wordBreak: 'break-word',
                display: '-webkit-box',
                WebkitLineClamp: isTool ? 2 : 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {data.title}
            </Typography>
            {data.subtitle ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {data.subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        {data.description ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: isTool ? 1 : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.35,
            }}
          >
            {data.description}
          </Typography>
        ) : null}

        <Stack
          direction="row"
          spacing={0.5}
          flexWrap="wrap"
          useFlexGap
          sx={{ rowGap: 0.5, maxHeight: isTool ? 26 : 52, overflow: 'hidden' }}
        >
          {isRoot ? <Chip size="small" color="primary" label="Root" /> : null}
          {data.category ? (
            <Chip
              size="small"
              variant="outlined"
              label={getToolCategoryLabel(data.category)}
              sx={{ borderColor: alpha(nodeColor, 0.65), color: nodeColor }}
            />
          ) : null}
          {typeof data.usageCount === 'number' ? (
            <Chip size="small" variant="outlined" label={`Used ${data.usageCount}`} />
          ) : null}
          {data.active === false || data.enabled === false ? (
            <Chip size="small" variant="outlined" label="Disabled" />
          ) : null}
          {data.dangerous ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<DangerousIcon sx={{ fontSize: 14 }} />}
              label="Dangerous"
            />
          ) : null}
        </Stack>
      </Stack>
      <Handle id="source-left" type="source" position={Position.Left} style={handleStyle} />
      <Handle id="source-right" type="source" position={Position.Right} style={handleStyle} />
      <Handle id="source-top" type="source" position={Position.Top} style={handleStyle} />
      <Handle id="source-bottom" type="source" position={Position.Bottom} style={handleStyle} />
    </Box>
  );
});

VisualizerNode.displayName = 'VisualizerNode';

const FlowCanvas: React.FC<{
  nodes: Node<VisualizerNodeData>[];
  edges: Edge[];
  graphKey: string;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
}> = ({ nodes, edges, graphKey, isFullscreen, onToggleFullscreen }) => {
  const theme = useTheme();
  const { fitView, setCenter } = useReactFlow();

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      fitView({ padding: 0.12, duration: 260 });
    }, 80);

    return () => window.clearTimeout(timeout);
  }, [fitView, graphKey]);

  const nodeTypes = useMemo(() => ({ visualizerNode: VisualizerNode }), []);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      fitView
      minZoom={0.03}
      maxZoom={1.35}
      nodesDraggable
      nodesConnectable={false}
      elementsSelectable
      proOptions={{ hideAttribution: true }}
      style={{
        background: theme.palette.mode === 'dark'
          ? theme.palette.background.default
          : alpha(theme.palette.grey[100], 0.72),
      }}
    >
      <Background
        variant={BackgroundVariant.Dots}
        gap={18}
        size={1}
        color={theme.palette.mode === 'dark' ? '#4b5563' : '#cbd5e1'}
      />
      <Panel position="top-left">
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            onClick={() => setCenter(0, 0, { zoom: 0.7, duration: 260 })}
          >
            Center agent
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => fitView({ padding: 0.12, duration: 260 })}
          >
            Fit all
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            onClick={onToggleFullscreen}
          >
            {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          </Button>
        </Stack>
      </Panel>
      <Controls showInteractive={false} position="bottom-left" />
      <MiniMap
        pannable
        zoomable
        position="bottom-right"
        nodeStrokeWidth={3}
        nodeColor={(node) => {
          const data = node.data as VisualizerNodeData;
          if (data.color) return data.color;
          if (data.kind === 'tool') return theme.palette.success.main;
          if (data.kind === 'root') return theme.palette.primary.main;
          return theme.palette.info.main;
        }}
      />
    </ReactFlow>
  );
};

export const AgentVisualizerCanvas: React.FC<AgentVisualizerCanvasProps> = ({ agent }) => {
  const theme = useTheme();
  const [includeDisabled, setIncludeDisabled] = useState(true);
  const [showTools, setShowTools] = useState(true);
  const [showSubAgents, setShowSubAgents] = useState(true);
  const [showSubAgentTools, setShowSubAgentTools] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const graphQuery = useQuery({
    queryKey: ['agent-visualizer-graph', agent.id, includeDisabled],
    queryFn: () => buildAgentGraph(agent, includeDisabled),
    enabled: Boolean(agent?.id),
  });

  const availableCategories = useMemo(() => {
    const categories = new Set<string>();
    (graphQuery.data?.toolConnections || []).forEach((connection) => {
      categories.add(connection.tool?.category || 'assigned_tools');
    });
    return Array.from(categories).sort((a, b) =>
      getToolCategoryLabel(a).localeCompare(getToolCategoryLabel(b))
    );
  }, [graphQuery.data]);

  const hasActiveFilters =
    search.trim() !== '' ||
    selectedCategories.length > 0 ||
    !showTools ||
    !showSubAgents ||
    showSubAgentTools;

  const toggleCategory = (category: string) => {
    setSelectedCategories((current) =>
      current.includes(category)
        ? current.filter((item) => item !== category)
        : [...current, category]
    );
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategories([]);
    setShowTools(true);
    setShowSubAgents(true);
    setShowSubAgentTools(false);
  };

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  const graphElements = useMemo(() => {
    const graph = graphQuery.data;
    if (!graph) return { nodes: [] as Node<VisualizerNodeData>[], edges: [] as Edge[] };

    const nodes: Node<VisualizerNodeData>[] = [];
    const edges: Edge[] = [];
    const nodeCenters = new Map<string, { x: number; y: number }>();
    const agentCenters = new Map<number, { x: number; y: number }>();
    const agentsById = new Map(graph.agents.map((graphAgent) => [graphAgent.id, graphAgent]));
    const subAgentChildren = new Map<number, SubAgentConnection[]>();
    const needle = normalizeNeedle(search);
    const categoryFilter = new Set(selectedCategories);
    const toolMatches = (connection: ToolConnection) => {
      if (!showTools) return false;
      if (connection.parentAgentId !== agent.id && !showSubAgentTools) return false;
      const category = connection.tool?.category || 'assigned_tools';
      if (categoryFilter.size > 0 && !categoryFilter.has(category)) return false;
      if (!needle) return true;
      return (
        matchesText(connection.assignment.tool_name, needle) ||
        matchesText(connection.tool?.description, needle) ||
        matchesText(connection.tool?.category, needle) ||
        matchesText(getToolCategoryLabel(category), needle)
      );
    };
    const subAgentMatches = (connection: SubAgentConnection) => {
      if (!showSubAgents) return false;
      if (!needle) return true;
      const subAgent = agentsById.get(connection.subAgentId);
      return (
        matchesText(subAgent?.name, needle) ||
        matchesText(subAgent?.description, needle) ||
        matchesText(subAgent?.agent_type, needle) ||
        matchesText(connection.assignment.effective_tool_name, needle) ||
        matchesText(connection.assignment.effective_tool_description, needle)
      );
    };
    const visibleSubAgentConnectionMap = new Map<string, SubAgentConnection>();
    graph.subAgentConnections.filter(subAgentMatches).forEach((connection) => {
      visibleSubAgentConnectionMap.set(connection.id, connection);
    });
    if (showSubAgents) {
      Array.from(visibleSubAgentConnectionMap.values()).forEach((connection) => {
        let currentAgentId = connection.parentAgentId;
        while (currentAgentId !== agent.id) {
          const parentConnection = graph.subAgentConnections.find(
            (candidate) => candidate.subAgentId === currentAgentId
          );
          if (!parentConnection) break;
          visibleSubAgentConnectionMap.set(parentConnection.id, parentConnection);
          currentAgentId = parentConnection.parentAgentId;
        }
      });
      graph.toolConnections.filter(toolMatches).forEach((connection) => {
        let currentAgentId = connection.parentAgentId;
        while (currentAgentId !== agent.id) {
          const parentConnection = graph.subAgentConnections.find(
            (candidate) => candidate.subAgentId === currentAgentId
          );
          if (!parentConnection) break;
          visibleSubAgentConnectionMap.set(parentConnection.id, parentConnection);
          currentAgentId = parentConnection.parentAgentId;
        }
      });
    }
    const visibleSubAgentConnections = Array.from(visibleSubAgentConnectionMap.values());
    const visibleAgentIds = new Set<number>([agent.id]);

    visibleSubAgentConnections.forEach((connection) => {
      if (connection.parentAgentId === connection.subAgentId) return;
      if (!subAgentChildren.has(connection.parentAgentId)) {
        subAgentChildren.set(connection.parentAgentId, []);
      }
      subAgentChildren.get(connection.parentAgentId)!.push(connection);
      visibleAgentIds.add(connection.parentAgentId);
      visibleAgentIds.add(connection.subAgentId);
    });

    agentCenters.set(agent.id, { x: 0, y: 0 });
    const queue = [agent.id];
    const positionedAgents = new Set<number>([agent.id]);

    while (queue.length > 0) {
      const parentId = queue.shift()!;
      const parentCenter = agentCenters.get(parentId) || { x: 0, y: 0 };
      const parentIsRoot = parentId === agent.id;
      const children = [...(subAgentChildren.get(parentId) || [])].sort((a, b) => {
        const left = agentsById.get(a.subAgentId)?.name || '';
        const right = agentsById.get(b.subAgentId)?.name || '';
        return left.localeCompare(right);
      });
      const visibleChildren = children.filter((child) => !positionedAgents.has(child.subAgentId));

      visibleChildren.forEach((child, index) => {
        const count = Math.max(1, visibleChildren.length);
        const orbitRadius =
          (parentIsRoot ? ROOT_SUB_AGENT_ORBIT_RADIUS : CHILD_SUB_AGENT_ORBIT_RADIUS) +
          Math.max(0, count - 4) * 90;
        const angle =
          count === 1
            ? 0
            : -Math.PI / 2 + (index * Math.PI * 2) / count;
        const center = {
          x: parentCenter.x + Math.cos(angle) * orbitRadius,
          y: parentCenter.y + Math.sin(angle) * orbitRadius,
        };
        agentCenters.set(child.subAgentId, center);
        positionedAgents.add(child.subAgentId);
        queue.push(child.subAgentId);
      });
    }

    graph.agents.filter((graphAgent) => visibleAgentIds.has(graphAgent.id)).forEach((graphAgent, index) => {
      if (!agentCenters.has(graphAgent.id)) {
        agentCenters.set(graphAgent.id, {
          x: Math.max(1, graphAgent.depth) * CHILD_AGENT_X_GAP,
          y: (index - (graph.agents.length - 1) / 2) * CHILD_AGENT_Y_GAP,
        });
      }
    });

    const addNode = (
      id: string,
      kind: AgentNodeKind,
      center: { x: number; y: number },
      data: Omit<VisualizerNodeData, 'kind'>
    ) => {
      nodeCenters.set(id, center);
      nodes.push({
        id,
        type: 'visualizerNode',
        position: toTopLeft(center, kind),
        data: {
          kind,
          ...data,
        },
      });
    };

    const addCurvedEdge = (
      edge: Edge,
      sourceCenter: { x: number; y: number },
      targetCenter: { x: number; y: number }
    ) => {
      edges.push({
        type: 'bezier',
        markerEnd: { type: MarkerType.ArrowClosed },
        ...getHandlePair(sourceCenter, targetCenter),
        ...edge,
      });
    };

    graph.agents.filter((graphAgent) => visibleAgentIds.has(graphAgent.id)).forEach((graphAgent) => {
      const isRoot = graphAgent.id === agent.id;
      addNode(`agent-${graphAgent.id}`, isRoot ? 'root' : 'agent', agentCenters.get(graphAgent.id)!, {
        title: graphAgent.name,
        subtitle: graphAgent.model_name || getAgentTypeLabel(graphAgent.agent_type),
        description: graphAgent.description,
        active: graphAgent.is_active,
        depth: graphAgent.depth,
      });
    });

    visibleSubAgentConnections.forEach((connection) => {
      const assignment = connection.assignment;
      const sourceNodeId = `agent-${connection.parentAgentId}`;
      let targetNodeId = `agent-${connection.subAgentId}`;
      const sourceCenter = nodeCenters.get(sourceNodeId) || { x: 0, y: 0 };
      let targetCenter = nodeCenters.get(targetNodeId);

      if (connection.parentAgentId === connection.subAgentId || !targetCenter) {
        const selfNodeId = `agent-${connection.parentAgentId}-self-${connection.assignment.id}`;
        const selfCenter = {
          x: sourceCenter.x,
          y: sourceCenter.y + CHILD_AGENT_Y_GAP,
        };
        addNode(selfNodeId, 'agent', selfCenter, {
          title: agentsById.get(connection.subAgentId)?.name || 'Self delegation',
          subtitle: 'Self delegation',
          description: assignment.effective_tool_description,
          active: assignment.is_enabled,
          enabled: assignment.is_enabled,
          depth: connection.depth + 1,
        });
        targetNodeId = selfNodeId;
        targetCenter = selfCenter;
      }

      addCurvedEdge(
        {
          id: connection.id,
          source: sourceNodeId,
          target: targetNodeId,
          animated: assignment.is_enabled && !connection.repeated,
          label: assignment.effective_tool_name,
          labelBgPadding: [6, 3],
          labelBgBorderRadius: 4,
          labelStyle: {
            fill: theme.palette.text.secondary,
            fontSize: 11,
            fontWeight: 600,
          },
          style: {
            stroke: assignment.is_enabled
              ? theme.palette.info.main
              : theme.palette.text.disabled,
            strokeWidth: 2.4,
            strokeDasharray: assignment.is_enabled ? undefined : '6 4',
          },
        },
        sourceCenter,
        targetCenter
      );
    });

    const toolsByParent = new Map<number, ToolConnection[]>();
    graph.toolConnections.filter(toolMatches).forEach((connection) => {
      if (!visibleAgentIds.has(connection.parentAgentId)) return;
      if (!toolsByParent.has(connection.parentAgentId)) {
        toolsByParent.set(connection.parentAgentId, []);
      }
      toolsByParent.get(connection.parentAgentId)!.push(connection);
    });

    Array.from(toolsByParent.entries()).forEach(([parentAgentId, connections]) => {
      const parentCenter = agentCenters.get(parentAgentId) || { x: 0, y: 0 };
      const groups = buildToolGroups(connections);
      const renderToolGroups = (sideGroups: ToolGroup[]) => {
        const centers = layoutToolGroups(parentCenter, sideGroups, parentAgentId === agent.id);

        sideGroups.forEach((group) => {
          const categoryColor = getCategoryColor(group.category);

          group.connections.forEach((connection) => {
            const toolCenter = centers.get(connection.id);
            if (!toolCenter) return;
            const tool = connection.tool;
            const toolNodeId = `tool-${connection.parentAgentId}-${sanitizeId(connection.assignment.tool_name)}`;

            addNode(toolNodeId, 'tool', toolCenter, {
              title: connection.assignment.tool_name,
              subtitle: tool?.category ? getToolCategoryLabel(tool.category) : 'Assigned tool',
              description: tool?.description || 'Tool assignment',
              enabled: connection.assignment.is_enabled,
              dangerous: Boolean(tool?.dangerous),
              category: tool?.category,
              usageCount: connection.assignment.usage_count || 0,
              depth: connection.depth,
              color: categoryColor,
            });

            addCurvedEdge(
              {
                id: connection.id,
                source: `agent-${parentAgentId}`,
                target: toolNodeId,
                animated: connection.assignment.is_enabled,
                style: {
                  stroke: connection.assignment.is_enabled
                    ? alpha(categoryColor, 0.86)
                    : theme.palette.text.disabled,
                  strokeWidth: 1.8,
                  strokeDasharray: connection.assignment.is_enabled ? undefined : '6 4',
                },
              },
              parentCenter,
              toolCenter
            );
          });
        });
      };

      renderToolGroups(groups);
    });

    const lockedNodeIds = new Set(
      nodes
        .filter((node) => node.data.kind === 'root' || node.data.kind === 'agent')
        .map((node) => node.id)
    );
    const resolvedNodes = resolveNodeCollisions(nodes, lockedNodeIds);
    const resolvedCenters = buildNodeCenterMap(resolvedNodes);

    return {
      nodes: resolvedNodes,
      edges: updateEdgeHandles(edges, resolvedCenters),
    };
  }, [
    agent.id,
    graphQuery.data,
    search,
    selectedCategories,
    showSubAgents,
    showSubAgentTools,
    showTools,
    theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.text.disabled,
    theme.palette.text.secondary,
    theme.palette.warning.main,
  ]);

  const stats = useMemo(() => {
    const graph = graphQuery.data;
    return {
      agents: graph?.agents.length || 0,
      subAgents: graph?.subAgentConnections.length || 0,
      tools: graph?.toolConnections.length || 0,
      disabled:
        (graph?.toolConnections.filter((edge) => !edge.assignment.is_enabled).length || 0) +
        (graph?.subAgentConnections.filter((edge) => !edge.assignment.is_enabled).length || 0),
    };
  }, [graphQuery.data]);

  const graphKey = [
    agent.id,
    includeDisabled,
    showTools,
    showSubAgents,
    showSubAgentTools,
    search,
    selectedCategories.join(','),
    stats.agents,
    stats.tools,
    stats.subAgents,
  ].join('-');

  return (
    <Stack spacing={2}>
      <SectionCard
        title="Agent Visualiser"
        icon={<GraphIcon fontSize="small" />}
        actions={
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={includeDisabled}
                  onChange={(event) => setIncludeDisabled(event.target.checked)}
                />
              }
              label={<Typography variant="caption">Include disabled</Typography>}
              sx={{ mr: 0.5 }}
            />
            <Tooltip title="Refresh graph">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => graphQuery.refetch()}
                  disabled={graphQuery.isFetching}
                >
                  Refresh
                </Button>
              </span>
            </Tooltip>
          </Stack>
        }
        description={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.75 }}>
            <Chip size="small" variant="outlined" color="primary" label={`${stats.agents} agents`} />
            <Chip size="small" variant="outlined" color="info" label={`${stats.subAgents} sub-agent links`} />
            <Chip size="small" variant="outlined" color="success" label={`${stats.tools} tools`} />
            {stats.disabled > 0 ? (
              <Chip size="small" variant="outlined" label={`${stats.disabled} disabled links`} />
            ) : null}
          </Stack>
        }
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <TextField
              size="small"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by tool, category, sub-agent, or name"
              sx={{ flex: 1, minWidth: { md: 360 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showTools}
                    onChange={(event) => {
                      setShowTools(event.target.checked);
                      if (!event.target.checked) {
                        setShowSubAgentTools(false);
                      }
                    }}
                  />
                }
                label={<Typography variant="caption">Tools</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showSubAgents}
                    onChange={(event) => {
                      setShowSubAgents(event.target.checked);
                      if (!event.target.checked) {
                        setShowSubAgentTools(false);
                      }
                    }}
                  />
                }
                label={<Typography variant="caption">Sub-agents</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showSubAgentTools}
                    onChange={(event) => setShowSubAgentTools(event.target.checked)}
                    disabled={!showTools || !showSubAgents}
                  />
                }
                label={<Typography variant="caption">Sub-agent tools</Typography>}
              />
              <Tooltip title="Clear visual filters">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ClearFilterIcon />}
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                  >
                    Clear
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          {availableCategories.length > 0 ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.75 }}>
              <Chip
                size="small"
                label={`All categories (${availableCategories.length})`}
                color={selectedCategories.length === 0 ? 'primary' : 'default'}
                variant={selectedCategories.length === 0 ? 'filled' : 'outlined'}
                onClick={() => setSelectedCategories([])}
                clickable
              />
              {availableCategories.map((category) => {
                const selected = selectedCategories.includes(category);
                const color = getCategoryColor(category);
                return (
                  <Chip
                    key={category}
                    size="small"
                    label={getToolCategoryLabel(category)}
                    onClick={() => toggleCategory(category)}
                    variant={selected ? 'filled' : 'outlined'}
                    clickable
                    sx={{
                      bgcolor: selected ? alpha(color, 0.22) : 'transparent',
                      borderColor: alpha(color, selected ? 0.9 : 0.55),
                      color,
                      '&:hover': {
                        bgcolor: alpha(color, 0.16),
                      },
                    }}
                  />
                );
              })}
            </Stack>
          ) : null}

          {graphQuery.error ? (
            <Alert severity="error">{getApiMessage(graphQuery.error)}</Alert>
          ) : null}

          {graphQuery.data?.warnings?.length ? (
            <Alert severity={graphQuery.data.truncated ? 'warning' : 'info'}>
              {graphQuery.data.warnings.slice(0, 3).join(' ')}
            </Alert>
          ) : null}

          <Portal disablePortal={!isFullscreen}>
            <Box
              sx={{
                height: isFullscreen ? '100vh' : { xs: 520, md: 640 },
                border: 1,
                borderColor: 'divider',
                borderRadius: isFullscreen ? 0 : 1.5,
                overflow: 'hidden',
                bgcolor: 'background.default',
                position: isFullscreen ? 'fixed' : 'relative',
                inset: isFullscreen ? 0 : 'auto',
                zIndex: isFullscreen ? 2147483000 : 'auto',
                '& .react-flow__controls': {
                  boxShadow: theme.shadows[2],
                },
                '& .react-flow__controls-button': {
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  borderBottomColor: 'divider',
                },
                '& .react-flow__minimap': {
                  bgcolor: alpha(theme.palette.background.paper, 0.92),
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                },
              }}
            >
              {graphQuery.isLoading ? (
                <Stack spacing={1.25} sx={{ p: 2 }}>
                  <Skeleton variant="rounded" height={130} />
                  <Skeleton variant="rounded" height={130} width="78%" />
                  <Skeleton variant="rounded" height={130} width="58%" />
                </Stack>
              ) : graphElements.nodes.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Alert severity="info">No graph data is available for this agent.</Alert>
                </Box>
              ) : (
                <ReactFlowProvider>
                  <FlowCanvas
                    nodes={graphElements.nodes}
                    edges={graphElements.edges}
                    graphKey={graphKey}
                    isFullscreen={isFullscreen}
                    onToggleFullscreen={() => setIsFullscreen((value) => !value)}
                  />
                </ReactFlowProvider>
              )}
            </Box>
          </Portal>
        </Stack>
      </SectionCard>
    </Stack>
  );
};

export default AgentVisualizerCanvas;
